import type { ComputedRef } from 'vue'
import type { KidDiscipline } from './interfaces'

import { useListsStore } from '@shared/model'
import { resolveUrl } from '@shared/lib/url'
import { AppRoute } from '@shared/config'

interface KidBalanceRow {
  label: string
  value: number
  tooltip: string
}

interface KidDisciplineBalanceOptions {
  discipline: ComputedRef<KidDiscipline>
}

interface KidDisciplineBalanceReturn {
  refTitle: ComputedRef<string>
  refUrl: ComputedRef<string>
  generalRows: ComputedRef<KidBalanceRow[]>
  bookingRows: ComputedRef<KidBalanceRow[]>
}

const TOOLTIP = {
  pace: 'Max amount of lessons\nper week for this child',
  unscheduled: 'Amount of lessons that\nare paid and committed,\nbut not yet confirmed\nor scheduled with the tutor',
  confirmed: 'All scheduled and\nconfirmed lessons\nfor this discipline',
  reserved: 'Future lessons\nreserved with a\ntutor. These will be\nconfirmed each\nmonth after rebill',
  completed: 'Amount of lessons\nfrom last purchase\nwith status\n"Completed"',
}

export const useKidDisciplineBalance = ({ discipline }: KidDisciplineBalanceOptions): KidDisciplineBalanceReturn => {
  const listsStore = useListsStore()

  const refTitle = computed<string>(() =>
    listsStore.getDiscipline?.code[discipline.value.code] ?? discipline.value.code,
  )

  const refUrl = computed<string>(() =>
    resolveUrl(AppRoute.DisciplinesGeneralInfo, { id: discipline.value.id }) ?? '',
  )

  const generalRows = computed<KidBalanceRow[]>(() => [
    { label: 'Pace', value: discipline.value.pace || 0, tooltip: TOOLTIP.pace },
    { label: 'Unscheduled', value: discipline.value.unscheduled_bookings, tooltip: TOOLTIP.unscheduled },
  ])

  const bookingRows = computed<KidBalanceRow[]>(() => {
    const info = discipline.value.bookings_info

    return [
      { label: 'Confirmed', value: info.confirmed, tooltip: TOOLTIP.confirmed },
      { label: 'Reserved', value: info.reserved, tooltip: TOOLTIP.reserved },
      { label: 'Completed', value: info.completed, tooltip: TOOLTIP.completed },
      {
        label: 'Skipped',
        value: info.no_show + info.canceled + info.rescheduled,
        tooltip: `No show: ${info.no_show}\nCanceled: ${info.canceled}\nRescheduled: ${info.rescheduled}`,
      },
    ]
  })

  return {
    refTitle,
    refUrl,
    generalRows,
    bookingRows,
  }
}
